import React, { FunctionComponent, useState } from "react";
import { Form, Select } from "antd";
import type { LabeledValue } from "antd/lib/select";
import FormLabel from "../FormLabel";
import ListLoadFailure from "./ListLoadFailure";
import styles from "../../BiformLayout.module.scss";
import { buildRules } from "../../formUtils";
import type { MasterListItem } from "../../apis";
import { V2Question } from "../../helper";
import { MasterListOption, useMasterListSearch, withCustomOption } from "../JobForm/useMasterListSearch";

/**
 * A company / role answer while it is being edited: the name the learner sees, which is what is sent
 * as the answer, and the picked entry's id, sent alongside as `otherFields.masterListId`.
 *
 * Company is closed to its list, so a pick carries that entry's id. Role is pick-or-type: a typed role
 * is its own id (value === label, as in the job form's Job Title). The id stays optional for reading
 * back a submission made before that, which still renders as its stored name.
 */
export interface MasterListValue {
  name: string;
  masterListId?: string;
}

export type MasterListSearch = (query: string) => Promise<{ items: MasterListItem[] }>;

/**
 * Where a picker's options come from. The two fields no longer share one mechanism:
 *  - `search` — Company, whose tenant list is searched server-side on every typing pause.
 *  - `list` — Role, whose whole master list is read once for the form and filtered in the browser,
 *    and which therefore has a load state of its own to report.
 */
export type MasterListSource =
  | { kind: "search"; search: MasterListSearch }
  | { kind: "list"; options: MasterListOption[]; loading: boolean; failed: boolean; onRetry: () => void };

interface MasterListSelectProps {
  // `id`, `value` and `onChange` are injected by the surrounding Form.Item.
  id?: string;
  value?: MasterListValue;
  onChange?: (value: MasterListValue | undefined) => void;
  placeholder: string;
  allowClear?: boolean;
  options: MasterListOption[];
  loading: boolean;
  onSearch: (term: string) => void;
  /** Called after a pick, so a caller holding the search term can clear it. */
  onPicked?: () => void;
  /** What the open dropdown says when it has no options to show. */
  notFoundContent: React.ReactNode;
}

/** What every variant of the input needs, whoever supplies its options. */
interface CommonInputProps {
  // `id`, `value` and `onChange` are injected by the surrounding Form.Item.
  id?: string;
  value?: MasterListValue;
  onChange?: (value: MasterListValue | undefined) => void;
  placeholder: string;
  /** Shown in place of the dropdown when the list is loaded and nothing matches. */
  emptyText: string;
  allowClear?: boolean;
}

interface MasterListInputProps extends CommonInputProps {
  source: MasterListSource;
  /** What is being picked, for the failure message ("role"). */
  noun: string;
  disabled?: boolean;
}

// `labelInValue`, so the chosen name keeps showing after a later search stops returning that entry —
// and so a saved submission whose company or role has since left the list still reads as its name.
const toSelectValue = (value?: MasterListValue): LabeledValue | undefined =>
  value?.name ? { value: value.masterListId ?? value.name, label: value.name } : undefined;

/**
 * The picker itself, closed to the options it is given.
 *
 * Company offers no "use what you typed" option, so its answers carry a real id from the list. Role's
 * caller appends one via `withCustomOption`, so a typed role arrives here as an option whose value is
 * its text. Typing narrows the options (`filterOption={false}`: the caller decides who narrows).
 */
const MasterListSelect: FunctionComponent<MasterListSelectProps> = ({
  id,
  value,
  onChange,
  placeholder,
  allowClear,
  options,
  loading,
  onSearch,
  onPicked,
  notFoundContent
}) => {
  const handleChange = (picked?: LabeledValue) => {
    onPicked?.();
    if (!picked) {
      onChange?.(undefined);
      return;
    }
    // The id is the option's value: a list entry's id, or the typed text for Role's custom option. The
    // label is read back off the option rather than off `picked`, which carries a rendered node on some paths.
    const fromList = options.find(option => option.value === picked.value);
    onChange?.({ name: fromList ? fromList.label : String(picked.value), masterListId: String(picked.value) });
  };

  return (
    <Select<LabeledValue>
      id={id}
      showSearch
      labelInValue
      allowClear={allowClear}
      filterOption={false}
      placeholder={placeholder}
      value={toSelectValue(value)}
      onSearch={onSearch}
      onChange={handleChange}
      loading={loading}
      options={options}
      notFoundContent={notFoundContent}
    />
  );
};

/** Company: the shared debounced server-side search hook, unchanged. */
const SearchedMasterListInput: FunctionComponent<CommonInputProps & { search: MasterListSearch }> = ({ search, emptyText, ...props }) => {
  const list = useMasterListSearch(search);
  return (
    <MasterListSelect
      {...props}
      options={list.options}
      loading={list.loading}
      onSearch={list.onSearch}
      notFoundContent={list.loading ? "Searching…" : emptyText}
    />
  );
};

/**
 * Role: the master list the form loaded once, filtered here on the typed term — no request per
 * keystroke, and no debounce to need, since nothing goes out. A term that matches no role is offered as
 * "use what you typed" (`withCustomOption`), so a role not on the list can still be answered.
 *
 * A failed load is said out loud in the dropdown as well as under the field. The term the learner has typed lives in this
 * component and survives a retry, which only re-runs the read.
 */
const ListedMasterListInput: FunctionComponent<
  CommonInputProps & { noun: string; options: MasterListOption[]; loading: boolean; failed: boolean; onRetry: () => void }
> = ({ noun, options, loading, failed, onRetry, emptyText, ...props }) => {
  const [term, setTerm] = useState("");
  const needle = term.trim().toLowerCase();
  const matches = needle ? options.filter(option => option.label.toLowerCase().includes(needle)) : options;

  let notFoundContent: React.ReactNode = emptyText;
  if (failed) notFoundContent = <ListLoadFailure noun={`${noun} list`} hint={`You can still type your ${noun}.`} onRetry={onRetry} />;
  else if (loading) notFoundContent = "Loading…";

  return (
    <MasterListSelect
      {...props}
      options={withCustomOption(matches, term)}
      loading={loading}
      onSearch={setTerm}
      onPicked={() => setTerm("")}
      notFoundContent={notFoundContent}
    />
  );
};

/**
 * Read-only (builder preview, admin viewing a response) shows the saved name without loading the
 * list: there is nothing to search when nothing can be changed.
 */
const MasterListInput: FunctionComponent<MasterListInputProps> = ({ disabled, source, noun, ...props }) => {
  if (disabled) return <Select<LabeledValue> id={props.id} disabled labelInValue value={toSelectValue(props.value)} placeholder={props.placeholder} />;
  if (source.kind === "search") return <SearchedMasterListInput {...props} search={source.search} />;
  return <ListedMasterListInput {...props} noun={noun} options={source.options} loading={source.loading} failed={source.failed} onRetry={source.onRetry} />;
};

interface MasterListFieldProps {
  question: V2Question;
  source: MasterListSource;
  /** What is being picked, for the placeholder, the empty state and the failure message. */
  noun: string;
  disabled?: boolean;
}

// The builder's default placeholder for a text question is "Enter", which reads wrong on a picker.
const resolvePlaceholder = (question: V2Question, noun: string) =>
  question.placeholder && question.placeholder !== "Enter" ? question.placeholder : `Select your ${noun}`;

/**
 * The empty state has to carry the explanation on a closed field: a learner whose required company is
 * not on the list cannot type their way past it, so it points at searching again with less, then at
 * the person who can add the entry. Role is open, so with nothing typed it just says to type one; once
 * something is typed the dropdown offers it instead of reaching this text.
 *
 * Never shown for a list that failed to load: `ListLoadFailure` takes over for that case.
 */
const resolveEmptyText = (noun: string, open: boolean) =>
  open ? `Type to add your ${noun}.` : `No ${noun} found. Try a shorter search, or ask your coordinator to have it added to the list.`;

/**
 * An off-campus request's Company or Role question. Company is picked only from its list; Role is picked
 * or typed. The question's own label, required flag and length rules apply, checked against the name,
 * which is what is submitted — so they also guard a typed role that is too long.
 *
 * A failed list is also reported under the field, not only inside the dropdown: a learner who never
 * opens a dropdown they cannot use would otherwise see a required field with no explanation at all.
 */
const MasterListField: FunctionComponent<MasterListFieldProps> = ({ question, source, noun, disabled = false }) => (
  <Form.Item
    name={question.id}
    label={<FormLabel label={question.label} required={question.isRequired} description={question.description} />}
    rules={buildRules(question).map(rule => ({ ...rule, transform: (value?: MasterListValue) => value?.name }))}
    extra={!disabled && source.kind === "list" && source.failed ? <ListLoadFailure noun={`${noun} list`} onRetry={source.onRetry} /> : undefined}
    // Read-only views show the saved answer like the other questions' read-only inputs, not greyed out.
    className={styles.disableSingleSelectInput}
  >
    <MasterListInput
      disabled={disabled}
      source={source}
      noun={noun}
      allowClear={!question.isRequired}
      placeholder={resolvePlaceholder(question, noun)}
      emptyText={resolveEmptyText(noun, source.kind === "list")}
    />
  </Form.Item>
);

export default MasterListField;
